import React, { useRef, useEffect, useState } from 'react';
import './WellnessHub.css';
import { Cloud, BarChart3, Zap, MessageSquare, Target, Users, Copy, Check } from 'lucide-react';

const WellnessHub = () => {
  const cardRefs = useRef([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.3 }
    );

    const elements = [
      sectionRef.current,
      ...cardRefs.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const [copiedCodes, setCopiedCodes] = useState({});
  const [visibleCodes, setVisibleCodes] = useState({});

  const partners = [
    {
      id: 1,
      name: 'Microsoft Azure',
      category: 'Cloud Infrastructure',
      description: 'Build, manage, and deploy applications anywhere using Azure\'s comprehensive cloud platform. Scale your infrastructure with enterprise-grade security and reliability.',
      icon: Cloud,
      website: 'https://azure.microsoft.com',
      color: '#0078D4',
      discount: '15%',
      redeemCode: 'AZURE15OFF'
    },
    {
      id: 2,
      name: 'Dynamics 365 Customer Service',
      category: 'Business Solutions',
      description: 'Deliver exceptional customer support across all channels with intelligent case management. Streamline operations and improve customer satisfaction rates.',
      icon: MessageSquare,
      website: '#',
      color: '#0066CC',
      discount: '20%',
      redeemCode: 'DYN365CS20'
    },
    {
      id: 3,
      name: 'Dynamics 365 Sales Enterprise',
      category: 'Sales Solutions',
      description: 'Accelerate sales performance with AI-driven insights and automated workflows. Close more deals faster with intelligent sales acceleration tools.',
      icon: BarChart3,
      website: '#',
      color: '#107C10',
      discount: '25%',
      redeemCode: 'SALES25OFF'
    },
    {
      id: 4,
      name: 'Bubble',
      category: 'No-Code Platform',
      description: 'Create powerful web applications without coding using visual programming tools. Launch scalable apps quickly with professional-grade functionality.',
      icon: Zap,
      website: 'https://bubble.io',
      color: '#1B4DFF',
      discount: '10%',
      redeemCode: 'BUBBLE10'
    },
    {
      id: 5,
      name: 'Dynamics 365 Team Member',
      category: 'Collaboration Tools',
      description: 'Enable seamless team collaboration with integrated access to business applications. Boost productivity through connected workplace solutions.',
      icon: Users,
      website: '#',
      color: '#8661C5',
      discount: '30%',
      redeemCode: 'TEAM30OFF'
    },
    {
      id: 6,
      name: 'Close CRM',
      category: 'Customer Relationship Management',
      description: 'Optimize your sales process with purpose-built CRM designed for growing teams. Track leads, manage pipelines, and close deals efficiently.',
      icon: Target,
      website: 'https://close.com',
      color: '#FF6B35',
      discount: '12%',
      redeemCode: 'CLOSECRM12'
    }
  ];

  const handleRedeemClick = (partnerId) => {
    setVisibleCodes(prev => ({
      ...prev,
      [partnerId]: !prev[partnerId]
    }));
  };

  const handleCopyCode = async (code, partnerId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodes(prev => ({ ...prev, [partnerId]: true }));
      setTimeout(() => {
        setCopiedCodes(prev => ({ ...prev, [partnerId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };


  return (
    <div className="wellness-hub-container">
      <div className="wellness-hub-header" ref={sectionRef}>
        <h2 className="wellness-hub-title">Wellness Hub</h2>
        <p className="wellness-hub-subtitle">
          Discover our trusted technology partners and business solutions designed to enhance your productivity and streamline your operations.
        </p>
      </div>

      <div className="wellness-hub-grid">
        {partners.map((partner, index) => {
          const IconComponent = partner.icon;
          
          return (
            <div
              key={partner.id}
              className="wellness-partner-card"
              ref={(el) => (cardRefs.current[index] = el)}
            >
              <div className="partner-card-header">
                <div className="partner-icon" style={{ backgroundColor: `${partner.color}15` }}>
                  <IconComponent 
                    size={24} 
                    color={partner.color}
                    strokeWidth={1.5}
                  />
                </div>
                <div className="partner-info">
                  <div className="partner-name-row">
                    <h3 className="partner-name">{partner.name}</h3>
                    <span className="partner-discount">{partner.discount} OFF</span>
                  </div>
                  <span className="partner-category">{partner.category}</span>
                </div>
              </div>

              <div className="partner-card-content">
                <p className="partner-description">{partner.description}</p>
                
                <button 
                  className={`redeem-button ${visibleCodes[partner.id] ? 'showing-code' : ''}`}
                  onClick={() => visibleCodes[partner.id] ? handleCopyCode(partner.redeemCode, partner.id) : handleRedeemClick(partner.id)}
                >
                  {visibleCodes[partner.id] ? (
                    <div className="button-code-content">
                      <span className="button-code-text">{partner.redeemCode}</span>
                      {copiedCodes[partner.id] ? (
                        <Check size={16} color="white" />
                      ) : (
                        <Copy size={16} color="white" />
                      )}
                    </div>
                  ) : (
                    'Redeem Code'
                  )}
                </button>
                
                {copiedCodes[partner.id] && visibleCodes[partner.id] && (
                  <div className="copy-success-inline">
                    Code copied to clipboard!
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="wellness-hub-footer">
        <p className="wellness-footer-text">
          Looking for additional business solutions? Contact our support team for personalized partner recommendations.
        </p>
      </div>
    </div>
  );
};

export default WellnessHub;
